import * as Yup from 'yup';

import { validatePhone } from '../../../utils/utils';

export interface AddBusinessFormValues {
  // Owner Details
  fullName: string;

  // Business Details
  businessName: string;
  businessCategory: string;
  otherCategoryName: string;
  businessEmail: string;
  phoneNumber: string;
  phoneCountryCode: string;
  phoneDialCode: string;
  phoneLocale: string;
  website: string;
  streetAddress: string;
  city: string;
  state: string;
  stateName: string;
  postalCode: string;
  country: string;
  countryName: string;
  latitude: number | null;
  longitude: number | null;

  // Billing Details
  sameAsBusiness: boolean;
  registeredBusinessName: string;
  gstNumber: string;
  billingEmail: string;
  billingPhoneNumber: string;
  billingPhoneCountryCode: string;
  billingPhoneDialCode: string;
  billingPhoneLocale: string;
  billingStreetAddress: string;
  billingCity: string;
  billingState: string;
  billingStateName: string;
  billingPostalCode: string;
  billingCountry: string;
  billingCountryName: string;
  billingLatitude: number | null;
  billingLongitude: number | null;
}

export const initialValues: AddBusinessFormValues = {
  // Owner Details
  fullName: '',

  // Business Details
  businessName: '',
  businessCategory: '',
  otherCategoryName: '',
  businessEmail: '',
  phoneNumber: '',
  phoneCountryCode: 'IN',
  phoneDialCode: '+91',
  phoneLocale: 'en-IN',
  website: '',
  streetAddress: '',
  city: '',
  state: '',
  stateName: '',
  postalCode: '',
  country: '',
  countryName: '',
  latitude: null,
  longitude: null,

  // Billing Details
  sameAsBusiness: false,
  registeredBusinessName: '',
  gstNumber: '',
  billingEmail: '',
  billingPhoneNumber: '',
  billingPhoneCountryCode: 'IN',
  billingPhoneDialCode: '+91',
  billingPhoneLocale: 'en-IN',
  billingStreetAddress: '',
  billingCity: '',
  billingState: '',
  billingStateName: '',
  billingPostalCode: '',
  billingCountry: '',
  billingCountryName: '',
  billingLatitude: null,
  billingLongitude: null,
};

export const addBusinessSchema = Yup.object().shape({
  // Owner Details
  fullName: Yup.string()
    .required('Full name is required')
    .min(2, 'Full name must be at least 2 characters')
    .max(100, 'Full name must be at most 100 characters'),

  // Business Details
  businessName: Yup.string()
    .required('Business name is required')
    .min(2, 'Business name must be at least 2 characters')
    .max(100, 'Business name must be at most 100 characters'),

  businessCategory: Yup.string().required('Please select a category'),

  otherCategoryName: Yup.string()
    .min(2, 'Category name must be at least 2 characters')
    .max(100, 'Category name must be at most 100 characters'),

  businessEmail: Yup.string()
    .required('Business email is required')
    .email('Please enter a valid email address'),

  phoneNumber: Yup.string()
    .required('Phone number is required')
    .test('is-valid-phone', 'Please enter a valid phone number', function (value) {
      if (!value) return true;
      const { phoneDialCode, phoneLocale } = this.parent;
      const fullPhoneNumber = `${phoneDialCode}${value}`;
      return validatePhone(fullPhoneNumber, phoneLocale);
    }),

  website: Yup.string().url('Please enter a valid URL'),

  streetAddress: Yup.string()
    .required('Street address is required')
    .min(5, 'Street address must be at least 5 characters'),

  city: Yup.string().required('City is required'),

  state: Yup.string().required('State is required'),

  postalCode: Yup.string()
    .required('PIN code is required')
    .matches(/^[A-Za-z0-9\s-]{3,10}$/, 'Please enter a valid PIN code'),

  country: Yup.string().required('Country is required'),

  // Billing Details
  sameAsBusiness: Yup.boolean(),

  registeredBusinessName: Yup.string().when('sameAsBusiness', {
    is: false,
    then: (schema) => schema.max(100, 'Name must be at most 100 characters'),
    otherwise: (schema) => schema.notRequired(),
  }),

  gstNumber: Yup.string(),

  billingEmail: Yup.string().when('sameAsBusiness', {
    is: false,
    then: (schema) =>
      schema.required('Billing email is required').email('Please enter a valid email'),
    otherwise: (schema) => schema.notRequired(),
  }),

  billingPhoneNumber: Yup.string().when('sameAsBusiness', {
    is: false,
    then: (schema) =>
      schema
        .required('Billing phone is required')
        .test('is-valid-billing-phone', 'Please enter a valid phone number', function (value) {
          if (!value) return true;
          const { billingPhoneDialCode, billingPhoneLocale } = this.parent;
          const fullPhoneNumber = `${billingPhoneDialCode}${value}`;
          return validatePhone(fullPhoneNumber, billingPhoneLocale);
        }),
    otherwise: (schema) => schema.notRequired(),
  }),

  billingStreetAddress: Yup.string().when('sameAsBusiness', {
    is: false,
    then: (schema) =>
      schema
        .required('Billing address is required')
        .min(5, 'Address must be at least 5 characters'),
    otherwise: (schema) => schema.notRequired(),
  }),

  billingCity: Yup.string().when('sameAsBusiness', {
    is: false,
    then: (schema) => schema.required('Billing city is required'),
    otherwise: (schema) => schema.notRequired(),
  }),

  billingState: Yup.string().when('sameAsBusiness', {
    is: false,
    then: (schema) => schema.required('Billing state is required'),
    otherwise: (schema) => schema.notRequired(),
  }),

  billingPostalCode: Yup.string().when('sameAsBusiness', {
    is: false,
    then: (schema) =>
      schema
        .required('Billing PIN code is required')
        .matches(/^[A-Za-z0-9\s-]{3,10}$/, 'Please enter a valid PIN code'),
    otherwise: (schema) => schema.notRequired(),
  }),

  billingCountry: Yup.string().when('sameAsBusiness', {
    is: false,
    then: (schema) => schema.required('Billing country is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
});

export default addBusinessSchema;
